import { FetchUserEncodedError } from "@lib/errors/FetchUserEncodedError";
import type {
  SHARED_CONSTANTS_DOMAIN_STATUS,
  SHARED_CONSTANTS_EMAIL_SELF_SERVE_ERRORS,
  SHARED_CONSTANTS_PREREQUISITE_STATUS,
} from "@lib/shared_constants";

export type DomainStatus = keyof SHARED_CONSTANTS_DOMAIN_STATUS;

export type PrerequisiteStatus = keyof SHARED_CONSTANTS_PREREQUISITE_STATUS;

export interface SendingDomain {
  id: string;
  domain: string;
  status: DomainStatus;
  appGroupIds: string[];
  autoAddedToAppGroups: boolean;
  mailFromDomain?: string;
  /**
   * SES configuration set name backing this sending domain. Two sending domains in the same
   * configuration set share its single tracking domain. Optional for backward compatibility with
   * older API responses that omit this field. Used by the frontend to resolve the effective
   * tracking domain for a sending domain.
   */
  configurationSetName?: string;
}

/**
 * Which system serves a tracking domain's clicks. "braze_ses" routes clicks through Braze/SES
 * (the default, and what legacy rows that omit the field imply); "partner" routes through a
 * third-party deep-linking partner (e.g. AppsFlyer, Branch) via a CNAME we provision.
 */
export type TrackingProvider = "braze_ses" | "partner";

export interface TrackingDomain {
  parentId: string;
  domain: string;
  status: DomainStatus;
  /** Defaults to "braze_ses" when omitted (legacy/non-partner rows). */
  trackingProvider?: TrackingProvider;
  /** Deep-linking partner identity (e.g. "appsflyer", "branch", or a custom name). Partner rows only. */
  partnerName?: string;
  /** CNAME target the partner domain points at (e.g. "yourbrand.onelink.me"). Partner rows only. */
  partnerCnameTarget?: string;
}

export interface IpPool {
  /** Full SES IP pool name used for API requests. */
  name: string;
  /** Customer-facing name with the SES subaccount prefix removed. */
  displayName: string;
  ipAddresses: string[];
  sendingDomains: SendingDomain[];
  trackingDomains: TrackingDomain[];
}

export interface DelegatedDomain {
  id: string;
  domain: string;
}

/**
 * IP-provision payload returned on every configuration_data status.
 * The frontend feature flipper decides whether to render the IP provisioning flow.
 */
export interface IpProvisionData {
  /** The company's dedicated IP allotment, set by its contract. */
  maxIps: number;
  /** Dedicated IPs the company holds across all its ESP accounts. */
  usedIps: number;
  /** Number of dedicated IPs the company can still add. */
  availableIps: number;
  /** SES subaccount resource id used to namespace IP pool names. */
  prefix: string;
}

/** Set when the run failed as a whole rather than pool by pool: preflight rejected the
 * submission, or the retries ran out. This is what the page's banner says. */
export interface IpProvisioningError {
  errorCode?: string;
  message?: string;
  /** Dedicated IPs still free in the region, when that is why the run failed. */
  freeIps?: number;
}

/** One pool from the most recent run, in the order the customer submitted them. */
export interface IpProvisioningPool {
  /** Prefixed SES pool name -- the same one IpPool carries, so the two lists join on it. */
  name: string;
  /** What the customer typed, which a rename-and-retry starts from. */
  submittedName: string;
  status: DomainStatus;
  /** The pool exists in SES, whether or not its IP addresses followed. A failed pool that was
   * created needs IP addresses added; one that was not needs creating. */
  created: boolean;
  /** Addresses the run put in the pool. Present on a pool the run created. */
  ipCount?: number;
  errorCode?: string;
  message?: string;
  freeIps?: number;
}

/**
 * The most recent provisioning run. Absent until the customer submits one, so its presence is
 * what tells the page there is something to report.
 */
export interface IpProvisioning {
  status: DomainStatus;
  startedAt: string;
  /** The run is still open but its job has stopped reporting in: stop polling. */
  stale: boolean;
  /** Nothing reached SES, so re-running the recommendation is the right thing to offer. */
  retryable: boolean;
  error: IpProvisioningError | null;
  pools: IpProvisioningPool[];
}

interface IpPoolsConfigurationData {
  ipPools: IpPool[];
  ipProvisionData: IpProvisionData;
  delegatedDomains: DelegatedDomain[];
  ipProvisioning?: IpProvisioning;
}

/** Every configuration state before sending domains can be edited. */
export interface PrerequisiteConfigurationData extends IpPoolsConfigurationData {
  status: Exclude<PrerequisiteStatus, "COMPLETED">;
}

export interface CompletedConfigurationData extends IpPoolsConfigurationData {
  status: SHARED_CONSTANTS_PREREQUISITE_STATUS["COMPLETED"];
}

export type ConfigurationData = PrerequisiteConfigurationData | CompletedConfigurationData;

export interface GetConfigurationDataResult {
  data: ConfigurationData;
}

export type NoSesAccountError = FetchUserEncodedError<SHARED_CONSTANTS_EMAIL_SELF_SERVE_ERRORS["NO_SES_ACCOUNT"]>;

export type MultipleSesAccountsError = FetchUserEncodedError<
  SHARED_CONSTANTS_EMAIL_SELF_SERVE_ERRORS["MULTIPLE_SES_ACCOUNTS"]
>;

export type GetConfigurationDataError = NoSesAccountError | MultipleSesAccountsError;

export const isGetConfigurationDataError = (error: unknown): error is GetConfigurationDataError =>
  error instanceof FetchUserEncodedError;
